import { batch } from 'react-redux';

import history from '@history';
import { authApi, authenticationObsoleteApi, userObsoleteApi } from 'app/services/api';
import accountsJwtService from 'app/services/jwtService';
import { getDeviceUuid } from 'app/services/secureTokenStore';
import _ from 'lodash';

import { DEFAULT_THEME_MODE } from 'constants/themes';
import { catchAsyncActionError } from 'utils/action';
import { getTpoCertResumeLoginUrl, isTpoCertPathname } from 'utils/tpoCertResume';

import {
  setDefaultSettingsAction,
  setSettingsAction,
  showMessageAction,
} from 'store/global/actions';

export const SET_MASTER_DATA = 'SET_MASTER_DATA';
export const REMOVE_MASTER_DATA = 'REMOVE_MASTER_DATA';
export const MASTER_LOGGED_OUT_ACTION_TYPE = 'MASTER_LOGGED_OUT_ACTION_TYPE';
export const EDIT_MASTER_SUCCESS = 'EDIT_MASTER_SUCCESS';
const CHANGE_MASTER_PASSWORD = 'CHANGE_MASTER_PASSWORD';

const loggedOutUserSettings = {
  layout: {
    config: {
      toolbar: {
        display: false,
      },
    },
  },
};

/**
 * Set User Data
 */
export const setMasterDataAction = master => dispatch => {
  dispatch({
    type: SET_MASTER_DATA,
    payload: master,
  });
  dispatch(setDefaultSettingsAction(master.data.settings));
};

/**
 * Logout
 */
export const logoutMasterAction =
  ({ isExplicitLogout, skipRedirect } = {}) =>
  dispatch => {
    batch(() => {
      if (!skipRedirect) {
        const tpoCertResumeLoginUrl = isTpoCertPathname(window.location.pathname)
          ? getTpoCertResumeLoginUrl()
          : null;

        // A passwordless TPO partner whose cert session times out cannot use the afr password
        // login — send them to their signup host's OTP login (resume is handled by the merged
        // DEV-3433/3531). Full-page nav: React Router cannot leave the SPA origin.
        if (tpoCertResumeLoginUrl) {
          window.location.href = tpoCertResumeLoginUrl;
        }

        if (!tpoCertResumeLoginUrl) {
          history.push({
            pathname: '/',
          });
        }
      }

      accountsJwtService.logout({ isExplicitLogout });

      dispatch({
        type: MASTER_LOGGED_OUT_ACTION_TYPE,
      });

      dispatch(setSettingsAction(_.merge({}, loggedOutUserSettings)));
    });
  };

export const editMasterProfileAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await userObsoleteApi.editProfile(params);

  const { email, firstName, lastName, sex, webTheme } = data;
  const { settings } = params;
  const fakeSettings = {
    ...settings,
    themeMode: webTheme ?? DEFAULT_THEME_MODE,
  };

  const payload = {
    email,
    firstName,
    lastName,
    sex,
    settings: fakeSettings,
  };

  dispatch(showMessageAction({ message: 'Successfully edited', variant: 'success' }));
  dispatch({ type: EDIT_MASTER_SUCCESS, payload });
  dispatch(setDefaultSettingsAction(fakeSettings));
});

export const setThemeModeAction = catchAsyncActionError(themeMode => async (dispatch, getState) => {
  // Flip the live theme by writing the FULL current settings (with the new themeMode) to
  // BOTH default and current. They must stay equal — DefaultLayout.configureRoute resets
  // current back to default whenever they differ, which would instantly revert the theme.
  // Carrying current.layout forward also preserves the header (a bare { themeMode } drops it).
  const { current } = getState().settings;
  dispatch(setDefaultSettingsAction({ ...current, themeMode }));

  const { data } = getState().auth.master;
  // master.data carries identity but not `sex`, so read it from the current user
  // to avoid clearing it on the silent /User/profile round-trip.
  const { data: currentUser } = await userObsoleteApi.getCurrentUser();

  // Persist the choice as `webTheme`; restore-on-login + a later profile save both read
  // this server value, so the theme survives a reload and a subsequent profile save.
  await userObsoleteApi.editProfile({
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    sex: currentUser?.sex,
    webTheme: themeMode,
  });
});

// TODO we need to rewrite this to use accountsAPI when BE is ready
export const changePasswordAction = catchAsyncActionError(params => async dispatch => {
  await authenticationObsoleteApi.changePassword(params);

  return dispatch({ type: CHANGE_MASTER_PASSWORD });
});

export const signOutCurrentDeviceAction = catchAsyncActionError(() => async dispatch => {
  await authApi.signOutDevice(getDeviceUuid());

  dispatch(showMessageAction({ message: 'Signed out from this device', variant: 'success' }));
  dispatch(logoutMasterAction({ isExplicitLogout: true }));
});
